import { RegionShared } from "@1games-ru/region-shared";
import { EstateImprovement } from "@1games-ru/region-shared/dist/enums/estate/estate";
import { InventoryOwnerType } from "@1games-ru/region-shared/dist/enums/inventory/inventory";
import { Inventory, InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useCallback, useMemo, useState } from "react";
import clsx from "clsx";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import { ChevronsUpIcon } from "@/shared/icons";
import { SoundButton } from "@/shared/ui";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import { inventoryMapSelector, safeSelector, setMemoizedItem } from "@/domains/inventory/slice";
import getDisabledItemsByFilters from "@/domains/inventory/helpers/getDisabledItemsByFilters";
import toast from "@/domains/toasts/helpers/toast";
import useInventoryManager from "@/domains/inventory/hooks/inventory/useInventoryManager";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useFilterItems from "@/domains/inventory/hooks/useFilterItems";
import useGetInventory from "@/domains/inventory/hooks/useGetInventory";
import useSearchItems from "@/domains/inventory/hooks/useSearchItems";
import InventoryContent from "@/domains/inventory/components/InventoryContent";
import InventoryFilter from "@/domains/inventory/components/InventoryFilter";
import InventoryFooter from "@/domains/inventory/components/InventoryFooter";
import InventoryHeader from "@/domains/inventory/components/InventoryHeader";
import InventorySearch from "@/domains/inventory/components/InventorySearch";
import InventoryWeight from "@/domains/inventory/components/InventoryWeight";
import InventoryWrapper from "@/domains/inventory/components/InventoryWrapper";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventoryGrid from "@/domains/inventory/components/dndkit/InventoryGrid";
import InventorySafeCellButton from "@/domains/inventory/components/safe/InventorySafeCellButton";
import InventorySafeHeader from "@/domains/inventory/components/safe/InventorySafeHeader";
import InventorySafeLoader from "@/domains/inventory/components/safe/InventorySafeLoader";
import InventorySafeUpgradeLevel from "@/domains/inventory/components/safe/InventorySafeUpgradeLevel";
import InventorySafeUpgradeLevelModal from "@/domains/inventory/components/safe/InventorySafeUpgradeLevelModal";

type Props = { inventoryId: number };

export default function InventoryCharacterSafeContainer({ inventoryId }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();
  const inventoryMap = useAppSelector(inventoryMapSelector);
  const inventoryCellsMap = useMemo(() => {
    const result: Record<string, Inventory> = {};
    Object.keys(inventoryMap).map((key) => {
      if (inventoryMap[key].ownerType === OwnerType.EstateSafe) {
        result[String(inventoryMap[key].id)] = inventoryMap[key];
      }
    });

    return result;
  }, [inventoryMap]);
  const [indexToInventoryId, setIndexToInventoryId] = useState<Record<string, number>>({});
  const [showUpgradeLevelModal, setShowUpgradeLevelModal] = useState(false);
  const safe = useAppSelector(safeSelector);
  const [currCellIndex, setCurrCellIndex] = useState(() => {
    let index = 0;
    if (safe) {
      const cell = safe.items.find((item) => item.id === inventoryId);
      index = cell?.index || 0;
    }
    setIndexToInventoryId({ [index]: inventoryId });

    return index;
  });
  const [prevCellIndex, setPrevCellIndex] = useState(currCellIndex);
  const [showLockScreen, setShowLockScreen] = useState(false);
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const inventory =
    inventoryCellsMap[String(indexToInventoryId[showLockScreen ? prevCellIndex : currCellIndex])];
  const { filteredItems, filterGroups, selectedGroups, onSelectHandler } = useFilterItems(
    inventory?.items || [],
  );
  const { onChangeSearchValue, deferredSearchValue, itemsBySearch } = useSearchItems(filteredItems);
  const dispatch = useAppDispatch();
  const { getInventory } = useGetInventory();
  const {
    moveItem,
    equipItem,
    stackItems,
    attachItem,
    applyItem,
    detachItem,
    dropItem,
    splitItem,
    getSafeState,
  } = useInventoryManager();
  const [buying, setBuying] = useState(false);

  const onClose = useCallback(() => {
    GameMpProvider.closeCurrentInterface();
  }, [inventoryId]);

  const onChangeCell = useCallback(
    async (index: number) => {
      if (index === currCellIndex) return;
      if (safe?.items) {
        const cell = safe.items.find((item) => item.index === index);

        if (cell) {
          if (cell.isBought) {
            try {
              setIsLoadingInventory(true);
              const response = await getInventory({
                inventoryOwnerId: String(cell.id),
                inventoryOwnerType: InventoryOwnerType.EstateSafe,
              });
              setIndexToInventoryId((state) => ({ ...state, [index]: response.id }));
            } catch (e) {
              console.log(e);
            } finally {
              onChangeSearchValue("");
              setPrevCellIndex(index);
              setIsLoadingInventory(false);
              setShowLockScreen(false);
            }
          } else {
            setShowLockScreen(true);
          }
          setCurrCellIndex(index);
        }
      }
    },
    [safe?.items, currCellIndex],
  );

  const toggleOpenUpgadeModal = useCallback(() => {
    setShowUpgradeLevelModal((state) => !state);
  }, []);

  const level = useMemo(() => {
    if (safe) {
      return safe.items.filter((item) => item.isBought).length;
    }
    return 0;
  }, [safe]);

  const improve = useCallback(async () => {
    try {
      setBuying(true);
      const data: typeof RegionShared.Backend.Requests.EstateImproveRequest = {
        estateId: safe.estateId,
        improvement: EstateImprovement.Safe,
      };
      const response = await RpcMpProvider.callServer(
        RegionShared.Backend.Events.Estate.EstateImprove,
        data,
      );

      if (response instanceof RpcMpProviderSuccessResponse) {
        const cell = safe.items.find((item) => item.index === currCellIndex);
        if (cell) {
          const response = await getInventory({
            inventoryOwnerId: String(cell.id),
            inventoryOwnerType: InventoryOwnerType.EstateSafe,
          });
          setIndexToInventoryId((state) => ({ ...state, [cell.index]: response.id }));
          await getSafeState({ estateId: safe.estateId });
          setPrevCellIndex(cell.index);
          if (showLockScreen) {
            setShowLockScreen(false);
          }
        }
        toggleOpenUpgadeModal();
      } else {
        toast.error(response.error.message);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setBuying(false);
    }
  }, [safe, currCellIndex, showLockScreen]);

  const momoizedItemHandler = useCallback(
    (item: InventoryItem | null) => dispatch(setMemoizedItem(item)),
    [],
  );

  const disabledItems = useMemo(() => {
    if (inventory) {
      return getDisabledItemsByFilters(inventory.items, itemsBySearch);
    }
    return [];
  }, [inventory, itemsBySearch]);

  if (!safe) return null;

  return (
    <>
      {showUpgradeLevelModal && safe && (
        <InventorySafeUpgradeLevelModal
          level={level}
          onBuy={improve}
          open
          improve={safe?.improvement}
          pending={buying}
          onClose={toggleOpenUpgadeModal}
        />
      )}
      <InventoryWrapper className="max-h-full">
        <InventoryHeader className="px-0">
          <InventorySafeHeader onClose={onClose} />
        </InventoryHeader>

        <InventoryContent className="flex flex-col mt-5">
          <div className="text-15 font-medium text-white/60">Ячейки для хранения</div>
          <div className="grid grid-cols-5 gap-1.5 mt-3" data-mz-accent="success">
            {safe.items.map((cell) => (
              <InventorySafeCellButton
                key={cell.id}
                label={String(cell.index + 1)}
                onClick={() => onChangeCell(cell.index)}
                isActive={cell.index === currCellIndex}
                isLocked={!cell.isBought}
              />
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 w-full">
            <InventorySearch
              value={deferredSearchValue}
              onChange={onChangeSearchValue}
              className="w-fit"
            />
            {filterGroups.length > 0 && (
              <InventoryFilter
                groups={filterGroups}
                selectedGroups={selectedGroups}
                onSelect={onSelectHandler}
              />
            )}
          </div>
        </InventoryContent>

        <InventoryContent className="relative flex-1 overflow-clip overflow-y-auto mt-5 pb-5">
          <InventorySafeUpgradeLevel
            level={level}
            className={clsx("absolute inset-0 z-10 bg-dark-900/90", {
              invisible: !showLockScreen,
            })}
          />
          <InventorySafeLoader
            className={clsx("absolute inset-0 z-10 bg-dark-900/90", {
              invisible: !isLoadingInventory,
            })}
          />
          {inventory && (
            <>
              <InventoryGrid
                disabled={showLockScreen}
                inventoryId={inventory.id}
                cols={inventory.size.width}
                rows={inventory.size.height}
                disabledItems={disabledItems}
                items={inventory.items}
                onMove={moveItem}
                onStack={stackItems}
                onAttach={attachItem}
                onMemoizedItem={momoizedItemHandler}
                onContextMenu={handleContextMenu}
              />
              {itemForContextMenu && isOpen && (
                <InventoryContextMenu
                  ref={menuRef}
                  position={position}
                  item={itemForContextMenu}
                  onApply={applyItem}
                  onDetach={detachItem}
                  onDrop={dropItem}
                  onEquip={equipItem}
                  onSplit={splitItem}
                  onClose={closeContextMenu}
                />
              )}
            </>
          )}
        </InventoryContent>

        <InventoryFooter>
          <InventoryWeight
            weight={inventory?.weight || 0}
            capacityWeight={inventory?.maxWeight || 0}
          />
          {safe.improvement && (
            <SoundButton
              data-mz-accent="success"
              className="h-[3rem] flex items-center gap-2.5 text-14 font-bold capitalize"
              onClick={toggleOpenUpgadeModal}
            >
              <ChevronsUpIcon size={12} className="text-white" />
              Купить улучшение
            </SoundButton>
          )}
        </InventoryFooter>
      </InventoryWrapper>
    </>
  );
}
